import { useEffect, useState } from "react";
import { CURRENCIES, BASE_CURRENCY_CODE } from "../config/currencyLocalization";
import {
  convertBetweenCurrencies,
  formatLocalizedAmount,
  resolveLocalizedCurrency,
  roundCurrencyAmount,
} from "../services/currencyLocalizationService";

const FALLBACK = {
  countryCode: "GB",
  currency: CURRENCIES[BASE_CURRENCY_CODE],
  rate: 1,
  rates: {},
};

const hasFxRates = (rates) =>
  rates && typeof rates === "object" && Object.keys(rates).length > 0;

const normalizeCode = (code) =>
  String(code || "")
    .trim()
    .toUpperCase();

export function useLocalizedCurrency(planCurrencyCode = null) {
  const [localization, setLocalization] = useState(FALLBACK);

  useEffect(() => {
    let isMounted = true;

    resolveLocalizedCurrency()
      .then((result) => {
        if (isMounted && result) {
          setLocalization(result);
        }
      })
      .catch(() => {
        if (isMounted) {
          setLocalization(FALLBACK);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const convert = (amount, fromCurrencyCode = planCurrencyCode) => {
    const from = normalizeCode(fromCurrencyCode || planCurrencyCode);
    const to = localization.currency?.code || BASE_CURRENCY_CODE;

    if (!from) {
      return roundCurrencyAmount(amount, to);
    }

    if (from === to) {
      return roundCurrencyAmount(amount, to);
    }

    if (!hasFxRates(localization.rates)) {
      return roundCurrencyAmount(amount, from);
    }

    return convertBetweenCurrencies(
      amount,
      from,
      to,
      localization.rates,
    );
  };

  const format = (amount, fromCurrencyCode = planCurrencyCode) => {
    const from = normalizeCode(fromCurrencyCode || planCurrencyCode);
    const to = localization.currency?.code || BASE_CURRENCY_CODE;

    if (!from || !hasFxRates(localization.rates) || from === to) {
      const currencyConfig =
        CURRENCIES[from || to] || CURRENCIES[BASE_CURRENCY_CODE];
      return formatLocalizedAmount(amount, currencyConfig);
    }

    return formatLocalizedAmount(
      convert(amount, from),
      localization.currency,
    );
  };

  return {
    ...localization,
    ratesReady: hasFxRates(localization.rates),
    convert,
    format,
  };
}
